import * as PIXI from "pixi.js";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { createSlug } from "../../createSlug";
import labelsData from "../../labels.json";

const InitPortfolioHero = (props) => {
  const { imageUrl, depthMapUrl, title } = props;
  const canvasRef = useRef(null);
  const appRef = useRef(null);
  const [isComponentMounted, setIsComponentMounted] = useState(true);
  const navigate = useNavigate(); // Use the useNavigate hook for navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50; // Minimum distance to recognize as swipe

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      // Check for Enter key
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleDropdownClick = (e) => {
    e.preventDefault(); // Prevent default to stop link behavior
    setIsDropdownOpen(!isDropdownOpen);

    // Delay the arrow animation to allow for state update
    setTimeout(() => {
      const arrow = document.querySelector(
        '.dropdown [data-toggle="dropdown"]:after'
      );
      if (arrow) {
        arrow.style.opacity = isDropdownOpen ? "0" : "1";
        arrow.style.transform = isDropdownOpen
          ? "rotate(-180deg)"
          : "rotate(0deg)";
      }
    }, 0);
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchMove = (e) => {
    const touchEndX = e.targetTouches[0].clientX;
    const touchEndY = e.targetTouches[0].clientY;
    setTouchEnd({ x: touchEndX, y: touchEndY });

    // Calculate the distance moved in each direction
    const xDistance = touchStart.x - touchEndX;
    const yDistance = touchStart.y - touchEndY;

    // Determine if the swipe is primarily horizontal or vertical
    if (Math.abs(xDistance) > Math.abs(yDistance)) {
      // Horizontal move: prevent default to avoid scrolling
      e.preventDefault();
    } else if (Math.abs(yDistance) > Math.abs(xDistance)) {
      // Vertical move: stop propagation to allow scrolling
      e.stopPropagation();
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const xDistance = touchStart.x - touchEnd.x;
    const yDistance = touchStart.y - touchEnd.y;

    // Check if the swipe is more horizontal than vertical
    if (Math.abs(xDistance) > Math.abs(yDistance)) {
      const isLeftSwipe = xDistance > minSwipeDistance;
      const isRightSwipe = xDistance < -minSwipeDistance;

      if (isLeftSwipe) {
        navigate(`/portfolio/${nextSlug}`);
      } else if (isRightSwipe) {
        navigate(`/portfolio/${prevSlug}`);
      }
    }

    // Reset touch positions
    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    const { imageUrl, depthMapUrl } = props;
    // Removed hardcoded depthMapUrl

    let image, depthMap;

    // Remove Hello message
    PIXI.utils.skipHello();

    // Initialize PIXI.Application only if it doesn't exist
    if (!appRef.current) {
      appRef.current = new PIXI.Application({
        autoResize: true,
        resolution: window.devicePixelRatio,
        backgroundAlpha: 0,
        // backgroundColor: 0x1099bb,
      });
    }

    const loader = new PIXI.Loader();

    loader.add("image", imageUrl).add("depthMap", depthMapUrl);

    const customCleanup = () => {
      if (appRef.current) {
        // Remove all children from the stage
        appRef.current.stage.removeChildren();

        // Destroy textures and base textures manually
        appRef.current.stage.children.forEach((child) => {
          if (child.texture) {
            child.texture.destroy(true); // true means to destroy the base texture as well
          }
        });
      }
    };

    const resize = () => {
      const containerWidth = appRef.current.view.clientWidth;
      const containerHeight = appRef.current.view.clientHeight;

      appRef.current.renderer.resize(containerWidth, containerHeight);

      const imageRatio = image.texture.width / image.texture.height;
      const containerRatio = containerWidth / containerHeight;

      if (containerRatio > imageRatio) {
        image.width = containerWidth;
        image.height = containerWidth / imageRatio;
      } else {
        image.height = containerHeight;
        image.width = containerHeight * imageRatio;
      }

      image.x = (containerWidth - image.width) / 2;
      image.y = (containerHeight - image.height) / 2;

      depthMap.width = image.width;
      depthMap.height = image.height;
      depthMap.x = image.x;
      depthMap.y = image.y;

      // Adjust gyroscope/accelerometer scaling
      const rect = appRef.current.view.getBoundingClientRect();
      const scalingFactor = 30;
      const scaleOffsetX = rect.width / scalingFactor;
      const displacementFilter = appRef.current.stage.filters[0];
      displacementFilter.scale.x = (image.width / 2 - scaleOffsetX) / 30;
    };

    const handleMouseMove = (e) => {
      if (!isComponentMounted || !appRef.current || !appRef.current.view)
        return; // Robust check
      const rect = appRef.current.view.getBoundingClientRect();
    };

    const handleOrientation = (event) => {
      const gamma = event.gamma; // Y-axis rotation in degrees [-90, 90]

      const rect = appRef.current.view.getBoundingClientRect();

      // Convert degrees to pixels for movement
      const localX = ((gamma / 90) * rect.width) / 3;

      const displacementFilter = appRef.current.stage.filters[0];
      displacementFilter.scale.x = (image.width / 2 - localX) / 30;

      // Handle text movement
      const heroText = document.querySelector(".hero-text");
      const textScalingFactor = 50;
      const textX = localX / textScalingFactor;
      if (heroText)
        heroText.style.transform = `translate(-50%, -50%) translateX(${textX}px)`;
    };

    loader.load((loader, resources) => {
      image = new PIXI.Sprite(resources.image.texture);
      appRef.current.stage.addChild(image);

      depthMap = new PIXI.Sprite(resources.depthMap.texture);
      appRef.current.stage.addChild(depthMap);

      const displacementFilter = new PIXI.filters.DisplacementFilter(depthMap);
      appRef.current.stage.filters = [displacementFilter];

      resize();

      window.addEventListener("resize", resize);
      if (window.DeviceOrientationEvent) {
        window.addEventListener("deviceorientation", handleOrientation);
      }
      window.addEventListener("mousemove", handleMouseMove);

      window.onmousemove = function (e) {
        const rect = appRef.current.view.getBoundingClientRect();
        const localX = e.clientX - rect.left - image.x;
        const localY = e.clientY - rect.top - image.y;

        displacementFilter.scale.x = (image.width / 2 - localX) / 30;
        displacementFilter.scale.y = (image.height / 2 - localY) / 30;

        // Handle text movement
        const heroText = document.querySelector(".hero-text");
        const textScalingFactor = 50;
        const textX = (e.clientX - rect.left) / textScalingFactor;
        const textY = (e.clientY - rect.top) / textScalingFactor;
        if (heroText)
          heroText.style.transform = `translate(-50%, -50%) translate(${textX}px, ${textY}px)`;
      };

      if (window.DeviceOrientationEvent) {
        window.addEventListener("deviceorientation", handleOrientation);
      }
    });

    if (canvasRef.current) {
      canvasRef.current.appendChild(appRef.current.view);
    }
    const handleClickOutside = (event) => {
      const dropdownMenu = document.querySelector(".dropdown");
      const checkbox = document.querySelector('input[type="checkbox"]');
      if (
        dropdownMenu &&
        !dropdownMenu.contains(event.target) &&
        checkbox !== event.target
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup function
    return () => {
      customCleanup();

      // Clear PIXI's texture cache
      PIXI.utils.clearTextureCache();

      // Remove canvas
      if (
        canvasRef.current &&
        canvasRef.current.contains(appRef.current.view)
      ) {
        canvasRef.current.removeChild(appRef.current.view);
      }
      // Remove event listeners
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("deviceorientation", handleOrientation);
    };
  }, [imageUrl, depthMapUrl]);

  // Calculate the previous and next slugs for navigation
  const currentSlug = window.location.pathname.split("/").pop();
  const currentTemplate = labelsData.find(
    (item) => createSlug(item.title) === currentSlug
  )?.template;
  const currentIndex = labelsData.findIndex(
    (item) => createSlug(item.title) === currentSlug
  );

  const createTemplateFriendlyMapping = (data) => {
    const mapping = {};
    data.forEach((item) => {
      if (!mapping[item.template]) {
        mapping[item.template] = item.friendly;
      }
    });
    return mapping;
  };

  const templateToFriendly = createTemplateFriendlyMapping(labelsData);

  const prevIndex = (currentIndex - 1 + labelsData.length) % labelsData.length;
  const nextIndex = (currentIndex + 1) % labelsData.length;

  const prevTemplate = labelsData[prevIndex].template;
  const nextTemplate = labelsData[nextIndex].template;

  const prevSlug = `${prevTemplate}/${createSlug(labelsData[prevIndex].title)}`;
  const nextSlug = `${nextTemplate}/${createSlug(labelsData[nextIndex].title)}`;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div
      ref={canvasRef}
      className="app"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        backgroundImage: `url("${props.imageUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="dropdown-case">
        <ul>
          <li className="dropdown">
            <input
              type="checkbox"
              checked={isDropdownOpen}
              onChange={() => setIsDropdownOpen(!isDropdownOpen)}
              onKeyDown={handleKeyDown} // Attach the updated event handler here
              tabIndex={0} // Make the element focusable
            />
            <a href="#" data-toggle="dropdown" onClick={handleDropdownClick}>
              {templateToFriendly[currentTemplate]}
            </a>
            <ul className="dropdown-menu">
              <li>
                <Link to={`/portfolio/${createSlug(currentTemplate)}`}>
                  All {templateToFriendly[currentTemplate]} Entries
                </Link>
              </li>
              <li className="menu-sub">
                <span className="menu-subtitle">More Categories:</span>
              </li>
              {Object.keys(templateToFriendly)
                .sort() // Sorting the categories alphabetically
                .filter((template) => template !== currentTemplate) // Removing the duplicate matching the same template you're on
                .map((template, index) => (
                  <li key={index}>
                    <Link
                      to={`/portfolio/${createSlug(template)}`}
                      onClick={() =>
                        navigate(`/portfolio/${createSlug(template)}`)
                      }
                    >
                      {templateToFriendly[template]}
                    </Link>
                  </li>
                ))}
            </ul>
          </li>
        </ul>
      </div>

      <h2 className="h2 hero-title hero-text">{props.title}</h2>

      <Link
        to={`/portfolio/${prevSlug}`}
        className="go-left active"
        title="Previous Portfolio Entry"
      >
        <ion-icon
          name="arrow-back-outline"
          role="img"
          aria-label="arrow left"
          className="md hydrated"
        ></ion-icon>
      </Link>
      <Link
        to={`/portfolio/${nextSlug}`}
        className="go-right active"
        title="Next Portfolio Entry"
      >
        <ion-icon
          name="arrow-forward-outline"
          role="img"
          aria-label="arrow right"
          className="md hydrated"
        ></ion-icon>
      </Link>

      <div className="swipe-instruction">
        <ion-icon
          name="return-up-back-outline"
          role="img"
          aria-label="return-up-back"
          className="md hydrated"
        ></ion-icon>
        swipe between cases
        <ion-icon
          name="return-up-forward-outline"
          role="img"
          aria-label="return-up-forward"
          className="md hydrated"
        ></ion-icon>
      </div>

      <svg
        className="full-screen-svg"
        width="100%"
        height="100%"
        viewBox="0 0 1200 226"
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: "absolute", top: 0, left: 0 }}
      >
        <rect width="100%" height="100%" fill="transparent" />
      </svg>

      <svg
        className="bottom-svg"
        width="1200"
        height="226"
        viewBox="0 0 1200 226"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M1200 226H1069.28C1079.33 223.22 1089.14 220.336 1098.87 217.898C1117.32 212.228 1133.97 204.752 1150.31 198.816C1158.56 196.054 1166.17 192.318 1173.28 188.441C1180.59 184.705 1187.53 181.113 1194.33 177.661C1196.25 176.686 1198.22 175.72 1199.99 174.745V226M1048.46 226H671.868C572 192.03 496 171.972 442.934 144.432C604.829 187.687 777.104 220.944 926.712 220.944C1033.28 220.944 1128.35 204.072 1199.99 161.912V171.972C1197.71 173.224 1195.32 174.339 1192.88 175.572C1185.92 178.895 1178.98 182.347 1171.67 185.799C1164.41 189.545 1156.94 193.138 1148.54 195.77C1132.21 201.44 1115.52 208.491 1097.1 213.738C1081.39 217.485 1065.34 222.401 1048.46 226ZM671.868 226H521.967H0V0C164 43.5 219.059 68.5032 323.09 114.32C345.746 123.433 368.394 133.54 392.017 142.239C415.473 151.106 438.934 160.365 463.359 168.384L499.605 180.7L536.49 192.03C560.918 199.912 586.164 205.73 610.909 212.494C631.145 217.55 651.566 221.782 671.868 226Z"></path>
      </svg>
    </div>
  );
};

export default InitPortfolioHero;
